import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useIsMobile } from '../../hooks/useDeviceDetection';
import { gameRoomService, type CreateRoomData } from '../../services/gameRoomService';
import { statisticsApi } from '../../services/statisticsApi';
import '../Dashboard/Dashboard.css';
import './CreateRoom.css';

const CreateRoomPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userLevel, setUserLevel] = useState(1);
  const [formData, setFormData] = useState<CreateRoomData>({
    name: '',
    gameType: 'kadi-ni-tidi',
    maxPlayers: 4,
    isPrivate: false,
    partnershipConfig: '4',
    password: '',
  });

  useEffect(() => {
    const fetchUserLevel = async () => {
      try {
        const userId = user?.id || user?._id;
        if (userId) {
          const globalStats = await statisticsApi.getPlayerGlobalStats(userId);
          const levelInfo = statisticsApi.calculatePlayerLevel(globalStats);
          setUserLevel(levelInfo.level || 1);
        }
      } catch (error) {
        console.error('Error fetching user level:', error);
      }
    };
    fetchUserLevel();
  }, [user]);

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Please enter a room name');
      return;
    }
    if (formData.isPrivate && (!formData.password || formData.password.length < 4)) {
      setError('Please enter a room password (at least 4 characters)');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await gameRoomService.createRoom(formData);
      if (response.success && response.data) {
        const roomId = response.data?.roomId || response.data?.id;
        if (roomId) {
          // Persist selected settings for immediate join so Colyseus initializes with correct options
          try {
            const selectedSettings = {
              maxPlayers: formData.maxPlayers,
              partnershipConfig: formData.partnershipConfig,
              timestamp: Date.now()
            };
            localStorage.setItem(`room_settings_${roomId}`, JSON.stringify(selectedSettings));
            // Also persist the front-end room id for consistency
            localStorage.setItem('frontendRoomId', roomId);
          } catch (e) {
            console.warn('⚠️ Failed to persist room settings cache:', e);
          }
          navigate(`/game/${roomId}`);
        } else {
          setError('Room created but no room ID returned');
        }
      } else {
        setError(response.message || 'Failed to create room');
      }
    } catch (err) {
      setError('An error occurred while creating the room');
      console.error('Create room error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modern-dashboard">
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <span className="brand-icon">🃏</span>
          <span className="brand-text">Card Game Platform</span>
        </div>
        
        <div className="header-actions">
          <div className="user-profile">
            <div className="user-avatar">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="user-info">
              <div className="user-name">{user?.username || 'Guest'}</div>
              <div className="user-level">Level {userLevel}</div>
            </div>
          </div>
        </div>
      </header>

      <main className="dashboard-content">
        <section className="welcome-section">
          <h1 className="welcome-greeting">Create Room</h1>
          <p className="welcome-subtitle">Set up your game room and invite friends!</p>
        </section>

        {error && (
          <div className="error-banner">
            <div className="error-message">{error}</div>
            <button onClick={() => setError(null)} className="btn-error-action">
              Dismiss
            </button>
          </div>
        )}

        <section className="stats-section">
          <div className="create-room-container">
            <form onSubmit={handleCreateRoom} className="create-room-form-modern">
              <div className="form-row">
                <div className="form-group-modern">
                  <label className="form-label-modern" htmlFor="room-name">Room Name</label>
                  <input
                    id="room-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter room name"
                    maxLength={50}
                    className="form-input-modern"
                    autoComplete="off"
                    required
                  />
                </div>

                <div className="form-group-modern">
                  <label className="form-label-modern" htmlFor="game-type">Game Type</label>
                  <select
                    id="game-type"
                    className="form-select-modern"
                    value={formData.gameType}
                    onChange={e => setFormData({ ...formData, gameType: e.target.value })}
                  >
                    <option value="kadi-ni-tidi">Kadi ni Tidi</option>
                    <option value="other-game">Other Game</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group-modern">
                  <label className="form-label-modern" htmlFor="max-players">Max Players</label>
                  <select
                    id="max-players"
                    className="form-select-modern"
                    value={formData.partnershipConfig}
                    onChange={e => {
                      const val = e.target.value;
                      // Handle special case for 6-player 2-deck variant
                      const players = val === '6-2deck' ? 6 : (parseInt(val.split('-')[0]) || 4);
                      setFormData({ ...formData, maxPlayers: players, partnershipConfig: val });
                    }}
                  >
                    {/* Common options for all devices */}
                    <option value="4">4 Players (Traditional)</option>
                    <option value="5">5 Players (2v3)</option>
                    {/*<option value="6">6 Players (3v3)</option>*/}
                    <option value="6-2p">6 Players (2v4)</option>
                    <option value="6-2deck">🆕 6 Players 2-Deck (3v3)</option>
                    
                    {/* Desktop-only options (7-10 players) */}
                    {!isMobile && (
                      <>
                        <option value="7">7 Players (3v4)</option>
                        {/*<option value="7-2p">7 Players (2v5)</option>*/}
                        {/*<option value="8">8 Players (4v4 Teams)</option>*/}
                        <option value="8-3p">8 Players (3v5)</option>
                        <option value="9">9 Players (4v5)</option>
                        {/*<option value="9-3p">9 Players (3v6)</option>*/}
                       {/*<option value="10">10 Players (5v5 Teams)</option>*/}
                        <option value="10-4p">10 Players (4v6)</option>
                      </>
                    )}
                  </select>
                </div>

                <div className="form-group-modern">
                  <label className="form-label-modern">Privacy</label>
                  <div className="privacy-toggle-modern">
                    <button
                      type="button"
                      className={`privacy-btn ${!formData.isPrivate ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, isPrivate: false })}
                    >
                      <span className="privacy-icon">🌐</span>
                      Public
                    </button>
                    <button
                      type="button"
                      className={`privacy-btn ${formData.isPrivate ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, isPrivate: true })}
                    >
                      <span className="privacy-icon">🔒</span>
                      Private
                    </button>
                  </div>
                </div>
              </div>

              {formData.isPrivate && (
                <div className="form-row">
                  <div className="form-group-modern">
                    <label className="form-label-modern" htmlFor="room-password">Room Password</label>
                    <input
                      id="room-password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter room password (min 4 characters)"
                      minLength={4}
                      className="form-input-modern"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => navigate('/rooms')}
                  className="btn-cancel"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-submit"
                >
                  {loading ? (
                    <>
                      <span className="spinner">⏳</span>
                      Creating Room...
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">✨</span>
                      Create & Enter Room
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CreateRoomPage;
